/**
 * DeployReconnectToast - Non-blocking toast for deploy reconnection progress
 *
 * Shows during server deployments when the WebSocket connection is being
 * re-established. Uses fast reconnect with progress indication.
 *
 * - Shows "Updating... (1/5)" with progress
 * - Auto-dismisses on successful reconnect
 * - Escalates to HardRefreshModal after exhausting attempts
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

interface DeployReconnectToastProps {
  /** Whether the toast should be visible */
  isVisible: boolean;
  /** Current reconnection attempt (1-based) */
  attempt: number;
  /** Maximum reconnection attempts */
  maxAttempts: number;
}

export function DeployReconnectToast({
  isVisible,
  attempt,
  maxAttempts,
}: DeployReconnectToastProps) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50"
        >
          <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-500/90 backdrop-blur-xl border border-amber-400/50 rounded-full shadow-2xl">
            <RefreshCw className="w-4 h-4 text-white animate-spin" />
            <span className="text-sm text-white font-medium">
              Updating...
            </span>
            <span className="text-xs text-white/80">
              ({attempt}/{maxAttempts})
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
